import * as Tabs from "@radix-ui/react-tabs"
import { FileTextIcon, CircleAlertIcon } from "lucide-react"

import { buttonVariants } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ClassificationsPanel } from "./ClassificationsPanel"
import { SearchTermsPanel } from "./SearchTermsPanel"
import { useDebouncedField } from "./useDebouncedField"

import { isTermValid, type SummarizerLabel } from "@/utils/summarizer-config"
import type { DocumentClassificationType } from "@/utils/types"

interface LabelDetailProps {
  label: SummarizerLabel
  classifications: DocumentClassificationType[]
  classificationsFailed?: boolean
  canWrite: boolean
  canDelete: boolean
  onChange: (updater: (label: SummarizerLabel) => SummarizerLabel) => void
}

export function LabelDetail({
  label,
  classifications,
  classificationsFailed = false,
  canWrite,
  canDelete,
  onChange,
}: LabelDetailProps) {
  const invalidTermCount = label.rawSearchTerms.filter((t) => !isTermValid(t)).length

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
      <div className="flex shrink-0 items-center justify-between gap-2">
        <h2 className="min-w-0 text-xl font-bold max-lg:[overflow-wrap:anywhere]">{label.label}</h2>
        <Popover>
          <PopoverTrigger
            type="button"
            className={buttonVariants({ variant: "ghost", size: "icon", className: "shrink-0" })}
            aria-label={canWrite ? "Edit description" : "View description"}
            title={canWrite ? "Edit description" : "View description"}
          >
            <FileTextIcon />
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-80 max-lg:max-w-(--radix-popover-content-available-width) max-lg:min-w-0"
          >
            <div className="flex flex-col gap-2">
              <label htmlFor="label-description" className="text-sm font-medium">
                Description
              </label>
              <DescriptionField
                value={label.description}
                readOnly={!canWrite}
                onCommit={(description) => onChange((current) => ({ ...current, description }))}
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <Tabs.Root defaultValue="classes" className="flex min-h-0 flex-1 flex-col gap-4">
        <Tabs.List aria-label="Label configuration" className="flex shrink-0 gap-2 border-b max-lg:flex-wrap">
          <Tabs.Trigger
            value="classes"
            className="-mb-px cursor-pointer border-b-2 border-transparent px-3 py-2 text-sm font-medium data-[state=active]:border-primary data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground max-lg:min-w-0 max-lg:flex-1 max-lg:px-2"
          >
            Document classes
          </Tabs.Trigger>
          <Tabs.Trigger
            value="terms"
            className="-mb-px cursor-pointer border-b-2 border-transparent px-3 py-2 text-sm font-medium data-[state=active]:border-primary data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground max-lg:min-w-0 max-lg:flex-1 max-lg:px-2"
          >
            Search terms ({label.rawSearchTerms.length})
            {invalidTermCount > 0 && (
              <CircleAlertIcon
                aria-label="Contains invalid search terms"
                className="ml-2 inline size-4 text-destructive"
              />
            )}
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="classes" className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <ClassificationsPanel
            label={label}
            classifications={classifications}
            classificationsFailed={classificationsFailed}
            canWrite={canWrite}
            onChange={onChange}
          />
        </Tabs.Content>
        <Tabs.Content value="terms" className="min-h-0 min-w-0 flex-1 overflow-y-auto">
          <SearchTermsPanel label={label} canWrite={canWrite} canDelete={canDelete} onChange={onChange} />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  )
}

function DescriptionField({
  value,
  readOnly,
  onCommit,
}: {
  value: string
  readOnly: boolean
  onCommit: (value: string) => void
}) {
  const field = useDebouncedField(value, onCommit)

  return (
    <textarea
      id="label-description"
      rows={3}
      value={field.value}
      readOnly={readOnly}
      onChange={(e) => field.onChange(e.target.value)}
      onBlur={field.flush}
      placeholder="Describe what this label is for"
      className="w-full rounded-md border border-input bg-transparent p-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
    />
  )
}
